/**
 * Statistical helpers for the channel framework (ENGINE-SPEC §3). Pure, dependency free.
 * Accuracy targets are modest (≈1e-7) — these feed evidence strengths, not proofs.
 */

export const mean = (a: ArrayLike<number>): number => {
	const n = a.length;
	if (!n) return 0;
	let s = 0;
	for (let i = 0; i < n; i++) s += a[i] as number;
	return s / n;
};

export const clamp = (x: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, x));

/** Population SD (the seed's `stdev`, AMB-08). */
export function sdPop(a: ArrayLike<number>): number {
	const n = a.length;
	if (n < 2) return 0;
	const m = mean(a);
	let s = 0;
	for (let i = 0; i < n; i++) s += ((a[i] as number) - m) ** 2;
	return Math.sqrt(s / n);
}

/** Sample SD (N−1). */
export function sdSample(a: ArrayLike<number>): number {
	const n = a.length;
	if (n < 2) return 0;
	const m = mean(a);
	let s = 0;
	for (let i = 0; i < n; i++) s += ((a[i] as number) - m) ** 2;
	return Math.sqrt(s / (n - 1));
}

/** Piecewise-linear ramp with flat extrapolation (the seed's `ramp`). */
export function ramp(x: number, pts: ReadonlyArray<readonly [number, number]>): number {
	const first = pts[0];
	const last = pts[pts.length - 1];
	if (!first || !last) return 0;
	if (x <= first[0]) return first[1];
	for (let i = 1; i < pts.length; i++) {
		const p1 = pts[i] as readonly [number, number];
		if (x <= p1[0]) {
			const p0 = pts[i - 1] as readonly [number, number];
			return p0[1] + ((p1[1] - p0[1]) * (x - p0[0])) / (p1[0] - p0[0]);
		}
	}
	return last[1];
}

/** Smallest/first x at which the ramp reaches `y` (searching from the left); null if never. */
export function rampInverse(
	pts: ReadonlyArray<readonly [number, number]>,
	y: number
): number | null {
	for (let i = 1; i < pts.length; i++) {
		const p0 = pts[i - 1] as readonly [number, number];
		const p1 = pts[i] as readonly [number, number];
		const lo = Math.min(p0[1], p1[1]);
		const hi = Math.max(p0[1], p1[1]);
		if (y >= lo && y <= hi && p0[1] !== p1[1])
			return p0[0] + ((p1[0] - p0[0]) * (y - p0[1])) / (p1[1] - p0[1]);
	}
	const f = pts[0];
	return f && f[1] === y ? f[0] : null;
}

/* ---------------- special functions ---------------- */

const LANCZOS = [
	57.1562356658629, -59.5979603554755, 14.1360979747417, -0.49191381609762, 3.3994649984811891e-5,
	4.6523628927048578e-5, -9.8374475304879565e-5, 1.5808870322491249e-4, -2.1026444172410488e-4,
	2.1743961811521265e-4, -1.6431810653676389e-4, 8.441822398385275e-5, -2.6190838401581408e-5,
	3.6899182659531625e-6,
];

export function lgamma(x: number): number {
	if (x <= 0) return Number.POSITIVE_INFINITY;
	let y = x;
	const tmp = x + 5.2421875;
	const t = (x + 0.5) * Math.log(tmp) - tmp;
	let ser = 0.9999999999999971;
	for (let j = 0; j < 14; j++) {
		y += 1;
		ser += (LANCZOS[j] as number) / y;
	}
	return t + Math.log((2.5066282746310007 * ser) / x);
}

/** Regularised lower incomplete gamma P(a, x). */
export function gammaP(a: number, x: number): number {
	if (x <= 0) return 0;
	if (a <= 0) return 1;
	if (x < a + 1) {
		let ap = a;
		let del = 1 / a;
		let sum = del;
		for (let n = 0; n < 500; n++) {
			ap += 1;
			del *= x / ap;
			sum += del;
			if (Math.abs(del) < Math.abs(sum) * 1e-14) break;
		}
		return sum * Math.exp(-x + a * Math.log(x) - lgamma(a));
	}
	// continued fraction for Q
	let b = x + 1 - a;
	let c = 1 / 1e-300;
	let d = 1 / b;
	let h = d;
	for (let i = 1; i < 500; i++) {
		const an = -i * (i - a);
		b += 2;
		d = an * d + b;
		if (Math.abs(d) < 1e-300) d = 1e-300;
		c = b + an / c;
		if (Math.abs(c) < 1e-300) c = 1e-300;
		d = 1 / d;
		const del = d * c;
		h *= del;
		if (Math.abs(del - 1) < 1e-14) break;
	}
	return 1 - Math.exp(-x + a * Math.log(x) - lgamma(a)) * h;
}

function betacf(a: number, b: number, x: number): number {
	const qab = a + b;
	const qap = a + 1;
	const qam = a - 1;
	let c = 1;
	let d = 1 - (qab * x) / qap;
	if (Math.abs(d) < 1e-300) d = 1e-300;
	d = 1 / d;
	let h = d;
	for (let m = 1; m <= 500; m++) {
		const m2 = 2 * m;
		let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
		d = 1 + aa * d;
		if (Math.abs(d) < 1e-300) d = 1e-300;
		c = 1 + aa / c;
		if (Math.abs(c) < 1e-300) c = 1e-300;
		d = 1 / d;
		h *= d * c;
		aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
		d = 1 + aa * d;
		if (Math.abs(d) < 1e-300) d = 1e-300;
		c = 1 + aa / c;
		if (Math.abs(c) < 1e-300) c = 1e-300;
		d = 1 / d;
		const del = d * c;
		h *= del;
		if (Math.abs(del - 1) < 1e-14) break;
	}
	return h;
}

/** Regularised incomplete beta I_x(a, b). */
export function betaI(x: number, a: number, b: number): number {
	if (x <= 0) return 0;
	if (x >= 1) return 1;
	const bt = Math.exp(
		lgamma(a + b) - lgamma(a) - lgamma(b) + a * Math.log(x) + b * Math.log(1 - x)
	);
	if (x < (a + 1) / (a + b + 2)) return (bt * betacf(a, b, x)) / a;
	return 1 - (bt * betacf(b, a, 1 - x)) / b;
}

/** Standard normal CDF. */
export function normalCdf(z: number): number {
	const t = 1 / (1 + 0.5 * Math.abs(z / Math.SQRT2));
	const tau =
		t *
		Math.exp(
			-((z * z) / 2) -
				1.26551223 +
				t *
					(1.00002368 +
						t *
							(0.37409196 +
								t *
									(0.09678418 +
										t *
											(-0.18628806 +
												t *
													(0.27886807 +
														t *
															(-1.13520398 +
																t * (1.48851587 + t * (-0.82215223 + t * 0.17087277))))))))
		);
	const erfc = tau; // erfc(|z|/√2)
	return z >= 0 ? 1 - 0.5 * erfc : 0.5 * erfc;
}

/** Standard normal quantile (Acklam's algorithm, refined once). */
export function normalQuantile(p: number): number {
	if (p <= 0) return Number.NEGATIVE_INFINITY;
	if (p >= 1) return Number.POSITIVE_INFINITY;
	const a = [
		-3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2,
		-3.066479806614716e1, 2.506628277459239,
	];
	const b = [
		-5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1,
		-1.328068155288572e1,
	];
	const c = [
		-7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734,
		4.374664141464968, 2.938163982698783,
	];
	const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];
	const plow = 0.02425;
	let x: number;
	if (p < plow) {
		const q = Math.sqrt(-2 * Math.log(p));
		x =
			((((((c[0] as number) * q + (c[1] as number)) * q + (c[2] as number)) * q +
				(c[3] as number)) *
				q +
				(c[4] as number)) *
				q +
				(c[5] as number)) /
			(((((d[0] as number) * q + (d[1] as number)) * q + (d[2] as number)) * q + (d[3] as number)) *
				q +
				1);
	} else if (p > 1 - plow) {
		const q = Math.sqrt(-2 * Math.log(1 - p));
		x =
			-(
				(((((c[0] as number) * q + (c[1] as number)) * q + (c[2] as number)) * q +
					(c[3] as number)) *
					q +
					(c[4] as number)) *
					q +
				(c[5] as number)
			) /
			(((((d[0] as number) * q + (d[1] as number)) * q + (d[2] as number)) * q + (d[3] as number)) *
				q +
				1);
	} else {
		const q = p - 0.5;
		const r = q * q;
		x =
			(((((((a[0] as number) * r + (a[1] as number)) * r + (a[2] as number)) * r +
				(a[3] as number)) *
				r +
				(a[4] as number)) *
				r +
				(a[5] as number)) *
				q) /
			((((((b[0] as number) * r + (b[1] as number)) * r + (b[2] as number)) * r +
				(b[3] as number)) *
				r +
				(b[4] as number)) *
				r +
				1);
	}
	return x;
}

/** Upper tail P(X ≥ k) for Poisson(mean) with real-valued k (continuous extension). */
export function poissonSf(k: number, mean: number): number {
	if (k <= 0) return 1;
	if (mean <= 0) return 0;
	return gammaP(k, mean);
}

/** Upper tail P(X ≥ k) for a quasi-Poisson count: NegBin(mean, variance = phi·mean). */
export function quasiPoissonSf(k: number, mean: number, phi: number): number {
	if (k <= 0) return 1;
	if (mean <= 0) return 0;
	if (phi <= 1.0001) return poissonSf(k, mean);
	const r = mean / (phi - 1); // size
	const q = mean / (r + mean); // failure prob
	return betaI(q, k, r);
}

/** Lower tail P(X ≤ k) for the same law. */
export function quasiPoissonCdf(k: number, mean: number, phi: number): number {
	if (k < 0) return 0;
	if (mean <= 0) return 1;
	return 1 - quasiPoissonSf(k + 1, mean, phi);
}

/** Quantile of the gamma(a, 1) distribution by bracketed bisection. */
export function gammaQuantile(a: number, p: number): number {
	if (p <= 0) return 0;
	let lo = 0;
	let hi = Math.max(1, a);
	while (gammaP(a, hi) < p) hi *= 2;
	for (let i = 0; i < 80; i++) {
		const mid = (lo + hi) / 2;
		if (gammaP(a, mid) < p) lo = mid;
		else hi = mid;
	}
	return (lo + hi) / 2;
}

/**
 * Memo for `garwood` (T-PERF-01): the gamma-quantile bisection costs ~160 incomplete-gamma evaluations and the
 * segment / spike / unit passes ask for the same small counts thousands of times. The function is pure, so
 * caching is exact; the cache is per level and bounded.
 */
const GARWOOD_CACHE = new Map<number, Map<number, [number, number]>>();
const GARWOOD_CACHE_MAX = 200_000;

/** Exact (Garwood) two-sided Poisson interval for a count K at `level`. */
export function garwood(K: number, level = 0.9): [number, number] {
	let byK = GARWOOD_CACHE.get(level);
	if (!byK) {
		byK = new Map();
		GARWOOD_CACHE.set(level, byK);
	}
	const hit = byK.get(K);
	if (hit) return [hit[0], hit[1]];
	const alpha = 1 - level;
	const lo = K <= 0 ? 0 : gammaQuantile(K, alpha / 2);
	const hi = gammaQuantile(K + 1, 1 - alpha / 2);
	if (byK.size < GARWOOD_CACHE_MAX) byK.set(K, [lo, hi]);
	return [lo, hi];
}

/** Exact-style interval for a binomial K of E (Clopper–Pearson via the beta CDF; E rounded). */
export function clopperPearson(K: number, E: number, level = 0.9): [number, number] {
	const alpha = 1 - level;
	const n = Math.max(1, E);
	const k = clamp(K, 0, n);
	const bq = (p: number, a: number, b: number) => {
		let lo = 0;
		let hi = 1;
		for (let i = 0; i < 60; i++) {
			const mid = (lo + hi) / 2;
			if (betaI(mid, a, b) < p) lo = mid;
			else hi = mid;
		}
		return (lo + hi) / 2;
	};
	const lo = k <= 0 ? 0 : bq(alpha / 2, k, n - k + 1);
	const hi = k >= n ? 1 : bq(1 - alpha / 2, k + 1, n - k);
	return [lo, hi];
}

/** Benjamini–Hochberg: returns, for each p, whether it is rejected at FDR q. */
export function benjaminiHochberg(p: ArrayLike<number>, q = 0.05): Uint8Array {
	const m = p.length;
	const out = new Uint8Array(m);
	if (!m) return out;
	const order = Array.from({ length: m }, (_, i) => i).sort(
		(a, b) => (p[a] as number) - (p[b] as number)
	);
	let cut = -1;
	for (let r = 0; r < m; r++) {
		if ((p[order[r] as number] as number) <= ((r + 1) / m) * q) cut = r;
	}
	for (let r = 0; r <= cut; r++) out[order[r] as number] = 1;
	return out;
}

/**
 * Chao–Shen coverage-adjusted entropy (nats) from a count vector. Reference: Chao & Shen (2003).
 * Returns 0 for < 2 observations.
 */
export function chaoShenEntropy(counts: ArrayLike<number>): number {
	let n = 0;
	let f1 = 0;
	for (let i = 0; i < counts.length; i++) {
		const c = counts[i] as number;
		n += c;
		if (c === 1) f1++;
	}
	if (n < 2) return 0;
	let cov = 1 - f1 / n;
	if (cov <= 0) cov = 1 - (f1 - 1) / n; // all singletons: avoid zero coverage
	if (cov <= 0) cov = 1 / n;
	let h = 0;
	for (let i = 0; i < counts.length; i++) {
		const c = counts[i] as number;
		if (c <= 0) continue;
		const pa = cov * (c / n);
		const denom = 1 - (1 - pa) ** n;
		h -= (pa * Math.log(pa)) / denom;
	}
	return h;
}
